/**
 * Reviews Hooks
 * Hooks for fetching and managing reviews
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@shared/lib/api/client'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import { useAuth } from '@shared/contexts'
import type { JobReview, ReviewStats, User } from '@shared/types'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types for API Response
// ============================================================================

interface ApiReviewOwner {
  _id: string
  name: string
  lastName: string
  email: string
  phoneNumber?: string
  profilePicture?: string
  [key: string]: unknown
}

interface ApiJobReview {
  _id: string
  owner?: ApiReviewOwner
  ownerId: string
  professional: string
  comment: string
  rate: number | { $numberDecimal: string }
  date: string
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

// ============================================================================
// Mappers
// ============================================================================

function mapApiReviewToJobReview(apiReview: ApiJobReview): JobReview {
  // Map owner to user
  const user: User | undefined = apiReview.owner
    ? {
        id: apiReview.owner._id,
        firstName: apiReview.owner.name || '',
        lastName: apiReview.owner.lastName || '',
        email: apiReview.owner.email || '',
        phone: apiReview.owner.phoneNumber || '',
        isEmailValidated: true,
        isPhoneValidated: false,
        isProfessional: false,
        isAccountValidated: true,
        createdAt: '',
        updatedAt: '',
      }
    : undefined

  // Handle MongoDB $numberDecimal format
  const rating = typeof apiReview.rate === 'object' && apiReview.rate !== null && '$numberDecimal' in apiReview.rate
    ? parseFloat(apiReview.rate.$numberDecimal)
    : typeof apiReview.rate === 'number'
    ? apiReview.rate
    : 0

  return {
    id: apiReview._id,
    professionalId: apiReview.professional,
    userId: apiReview.ownerId,
    rating,
    comment: apiReview.comment,
    jobDate: apiReview.date,
    createdAt: apiReview.date || apiReview.createdAt || new Date().toISOString(),
    updatedAt: apiReview.updatedAt || new Date().toISOString(),
    user,
  }
}

// ============================================================================
// Query Keys
// ============================================================================

export const reviewsKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewsKeys.all, 'list'] as const,
  list: (filters: string) => [...reviewsKeys.lists(), filters] as const,
  byProfessional: (professionalId: string) => [...reviewsKeys.all, 'professional', professionalId] as const,
  stats: (professionalId: string) => [...reviewsKeys.all, 'stats', professionalId] as const,
}

// ============================================================================
// Types
// ============================================================================

export interface CreateReviewData {
  professionalId: string
  rating: number // 1-5
  comment?: string
  jobDate?: string
}

export interface UpdateReviewData {
  rating?: number
  comment?: string
  jobDate?: string
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get reviews for a specific professional
 * @param professionalId - Professional ID
 * @returns List of reviews with populated user data
 */
export function useReviewsByProfessional(professionalId: string) {
  return useQuery({
    queryKey: reviewsKeys.byProfessional(professionalId),
    queryFn: async () => {
      const response = await apiClient.get<ApiJobReview[]>(
        API_ENDPOINTS.reviews.byProfessional(professionalId)
      )
      return response.map(mapApiReviewToJobReview)
    },
    enabled: !!professionalId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Calculate review statistics for a professional
 * @param professionalId - Professional ID
 * @returns Average rating and rating distribution
 */
export function useReviewStats(professionalId: string) {
  const { data: reviews } = useReviewsByProfessional(professionalId)

  const stats: ReviewStats = {
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
  }

  if (!reviews || reviews.length === 0) {
    return stats
  }

  // Calculate distribution
  reviews.forEach((review) => {
    const rating = review.rating as 1 | 2 | 3 | 4 | 5
    stats.ratingDistribution[rating]++
  })

  // Calculate average
  const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0)
  stats.averageRating = totalRating / reviews.length
  stats.totalReviews = reviews.length

  return stats
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new review
 * Implements optimistic updates for immediate UI feedback
 */
export function useCreateReview() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: (data: CreateReviewData) => {
      // TODO: BACKEND REFACTOR NEEDED
      // The backend currently expects an array of reviews instead of a single review object.
      // This is nonsensical since we always create one review at a time.
      // Future improvement: Update backend to accept single review object instead of array.
      // Backend endpoint: POST /api/jobsreviews
      // Current format: [{ owner, comment, rate, date, professional }]
      // Desired format: { userId, comment, rating, jobDate, professionalId }

      if (!user?.id) {
        throw new Error('User must be authenticated to create a review')
      }

      // Map frontend data structure to backend expectations
      const backendReviewFormat = {
        owner: user.id,
        comment: data.comment?.trim() || '',
        rate: data.rating,
        date: data.jobDate ? new Date(data.jobDate) : new Date(),
        professional: data.professionalId
      }

      // Wrap in array because backend incorrectly expects array format
      return apiClient.post<JobReview>(API_ENDPOINTS.reviews.create, [backendReviewFormat])
    },

    // Optimistic update: show review immediately before server response
    onMutate: async (newReviewData) => {
      // Cancel any outgoing refetches for this professional's reviews
      await queryClient.cancelQueries({
        queryKey: reviewsKeys.byProfessional(newReviewData.professionalId),
      })

      // Snapshot previous state for rollback on error
      const previousReviews = queryClient.getQueryData(
        reviewsKeys.byProfessional(newReviewData.professionalId)
      )

      // Create optimistic review object
      const optimisticReview: JobReview = {
        id: `temp-${Date.now()}`, // Temporary ID
        professionalId: newReviewData.professionalId,
        userId: user!.id,
        rating: newReviewData.rating,
        comment: newReviewData.comment || '',
        jobDate: newReviewData.jobDate || new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: {
          id: user!.id,
          firstName: user!.firstName,
          lastName: user!.lastName,
          email: user!.email,
          phone: user!.phone || '',
          city: user!.city,
          cityId: user!.cityId,
          isEmailValidated: user!.isEmailValidated,
          isPhoneValidated: user!.isPhoneValidated || false,
          isProfessional: false,
          isAccountValidated: user!.isAccountValidated,
          createdAt: user!.createdAt,
          updatedAt: user!.updatedAt,
        },
      }

      // Update cache with optimistic review (add to start of list)
      queryClient.setQueryData(
        reviewsKeys.byProfessional(newReviewData.professionalId),
        (old: JobReview[] | undefined) => [optimisticReview, ...(old || [])]
      )

      // Return context for rollback
      return {
        previousReviews,
        professionalId: newReviewData.professionalId,
      }
    },

    // Rollback on error
    onError: (err, _newReviewData, context) => {
      if (context?.previousReviews) {
        queryClient.setQueryData(
          reviewsKeys.byProfessional(context.professionalId),
          context.previousReviews
        )
      }
      logger.error('Failed to create review:', err)
    },

    // Refetch after mutation completes (success or error) to ensure consistency
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewsKeys.byProfessional(variables.professionalId),
      })
      void queryClient.invalidateQueries({
        queryKey: ['professionals'],
      })
    },
  })
}

/**
 * Update an existing review
 * DEPRECATED: Edit functionality has been removed. Reviews are immutable after creation.
 * This hook is kept for potential future use but is no longer used in the UI.
 */
// export function useUpdateReview(reviewId: string) {
//   const queryClient = useQueryClient()
//
//   return useMutation({
//     mutationFn: (data: UpdateReviewData) => {
//       return apiClient.put<JobReview>(API_ENDPOINTS.reviews.update(reviewId), data)
//     },
//     onSuccess: (updatedReview) => {
//       // Invalidate reviews
//       queryClient.invalidateQueries({
//         queryKey: reviewsKeys.byProfessional(updatedReview.professionalId),
//       })
//       queryClient.invalidateQueries({
//         queryKey: ['professionals'],
//       })
//     },
//   })
// }

/**
 * Interface for delete review mutation
 */
interface DeleteReviewData {
  reviewId: string
  professionalId: string
}

/**
 * Delete a review
 * Implements optimistic updates for immediate UI feedback
 */
export function useDeleteReview() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: ({ reviewId }: DeleteReviewData) => {
      // TODO: BACKEND REFACTOR NEEDED
      // The backend currently requires owner ID as query parameter for ownership validation.
      // Future improvement: Extract owner ID from JWT token server-side instead of requiring it as parameter.
      // Backend endpoint: DELETE /api/jobsreviews/:reviewId?owner=:ownerId
      // Current format: /api/jobsreviews/123?owner=user456
      // Desired format: /api/jobsreviews/123 (with owner extracted from JWT token)

      if (!user?.id) {
        throw new Error('User must be authenticated to delete a review')
      }

      return apiClient.delete(`${API_ENDPOINTS.reviews.delete(reviewId)}?owner=${user.id}`)
    },

    // Optimistic update: remove review immediately before server response
    onMutate: async ({ reviewId, professionalId }) => {
      // Cancel any outgoing refetches for this professional's reviews
      await queryClient.cancelQueries({
        queryKey: reviewsKeys.byProfessional(professionalId),
      })

      // Snapshot previous state for rollback on error
      const previousReviews = queryClient.getQueryData(
        reviewsKeys.byProfessional(professionalId)
      )

      // Remove review optimistically from cache
      queryClient.setQueryData(
        reviewsKeys.byProfessional(professionalId),
        (old: JobReview[] | undefined) =>
          old?.filter((review) => review.id !== reviewId) || []
      )

      // Return context for rollback
      return {
        previousReviews,
        professionalId,
      }
    },

    // Rollback on error
    onError: (err, _variables, context) => {
      if (context?.previousReviews) {
        queryClient.setQueryData(
          reviewsKeys.byProfessional(context.professionalId),
          context.previousReviews
        )
      }
      logger.error('Failed to delete review:', err)
    },

    // Refetch after mutation completes (success or error) to ensure consistency
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewsKeys.byProfessional(variables.professionalId),
      })
      void queryClient.invalidateQueries({
        queryKey: ['professionals'],
      })
    },
  })
}
